import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useSearchParams } from "react-router-dom";
import { STAGE_COLORS, STAGE_LABELS } from "../lib/stages";
import { FlagPip } from "../components/StageBadge";
import RoomDrawer from "../components/RoomDrawer";
import MobileFilterPanel from "../components/MobileFilterPanel";
import { useApplication } from "../context/ApplicationContext";
import { EmptyState } from "../components/OperationalUI";
import { AssignmentBadge } from "../components/Assignment";
import { repairLocation } from "../lib/location";

export default function Kanban() {
  const [params, setParams] = useSearchParams();
  const [rooms, setRooms] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mobileStage, setMobileStage] = useState("");
  const { effectiveWorkflow } = useApplication();
  const blockId = params.get("block") || "";

  const load = useCallback(async () => {
    setLoading(true);
    const q = blockId ? `?block_id=${blockId}` : "";
    setError("");
    try {
      const [r, b] = await Promise.all([api.get(`/dashboard/kanban${q}`), api.get("/blocks")]);
      setRooms(Array.isArray(r.data) ? r.data : []); setBlocks(Array.isArray(b.data) ? b.data : []);
    } catch (e) {
      setError(e.response?.data?.detail || "Kanban could not be loaded. Please try again.");
    } finally { setLoading(false); }
  }, [blockId]);
  useEffect(() => {
    load();
  }, [load]);

  const columnStages = effectiveWorkflow?.stages?.filter(s=>s.enabled && s.kanban_visible !== false) || [];
  const grouped = columnStages.map((definition) => ({
    stage: definition.key, definition,
    rooms: rooms.filter((r) => r.current_stage === definition.key),
  }));

  return (
    <div data-testid="kanban-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-chivo font-black text-4xl tracking-tight">
            Kanban
          </h1>
        </div>
        <MobileFilterPanel className="bg-white border border-gray-200 p-3 flex-wrap gap-3 items-end" testId="kanban-filters">
          <label className="block text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-1">
            Filter block
          </label>
          <select
            value={blockId}
            onChange={(e) => setParams(e.target.value ? { block: e.target.value } : {})}
            className="border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-black bg-white"
            data-testid="block-filter"
          >
            <option value="">All blocks</option>
            {blocks.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </MobileFilterPanel>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : error ? (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">{error} <button onClick={load} className="ml-2 underline">Retry</button></div>
      ) : rooms.length === 0 ? (
        <EmptyState title="No work in this scope" description="There are no Rooms to show for the selected Site and Block." />
      ) : (
        <><label className="mb-4 block text-sm font-semibold md:hidden">Stage<select value={mobileStage} onChange={e=>setMobileStage(e.target.value)} className="mt-1 min-h-11 w-full rounded-lg border bg-white px-3"><option value="">All stages</option>{grouped.map(({stage,definition})=><option key={stage} value={stage}>{definition.display_name||definition.name}</option>)}</select></label><div className="kanban-scroll flex max-h-[calc(100vh-13rem)] gap-4 overflow-auto overscroll-contain rounded-xl border bg-slate-50 p-3">
          {grouped.map(({ stage, definition, rooms }) => {
            const c = STAGE_COLORS[stage] || {text: definition.colour || "#475569", bg:"#f8fafc", border:"#cbd5e1"};
            return (
              <div
                key={stage}
                className={`w-full shrink-0 flex-col md:flex md:w-[280px] ${mobileStage&&mobileStage!==stage?"hidden":"flex"}`}
                data-testid={`kanban-column-${stage}`}
              >
                <div
                  className="sticky top-0 z-10 border-t-4 bg-white border border-gray-200 p-3 shadow-sm"
                  style={{ borderTopColor: c.text }}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className="text-xs font-chivo font-black uppercase tracking-wider"
                      style={{ color: c.text }}
                    >
                      {definition.display_name || definition.name || STAGE_LABELS[stage] || "Workflow stage"}
                    </span>
                    <span
                      className="text-xs font-mono font-bold px-2 py-0.5 border"
                      style={{
                        color: c.text,
                        backgroundColor: c.bg,
                        borderColor: c.border,
                      }}
                    >
                      {rooms.length}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 mt-2 min-h-[200px]">
                  {rooms.map((r) => (
                    <KanbanCard
                      key={r.id}
                      room={r}
                      onClick={() => setSelectedId(r.id)}
                    />
                  ))}
                  {!rooms.length&&<div className="rounded-lg border border-dashed bg-slate-50 p-4 text-center text-xs text-slate-500">No Rooms at this stage</div>}
                </div>
              </div>
            );
          })}
        </div></>
      )}

      {selectedId && (
        <RoomDrawer
          roomId={selectedId}
          onClose={() => {
            setSelectedId(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function KanbanCard({ room, onClick }) {
  const flags = room.flags || {};
  const location=repairLocation({...room,room_name:room.name});
  return (
    <button
      onClick={onClick}
      className="bg-white border border-gray-200 p-3 text-left hover:border-black transition-colors duration-150"
      data-testid={`kanban-card-${room.name.replace(/\s+/g, "-")}`}
    >
      <div className="flex justify-between items-start gap-2">
        <div className="font-chivo font-bold text-sm">{location.primary}</div>
        {room.type === "common" && (
          <span className="text-[9px] font-mono font-bold text-gray-500 uppercase border border-gray-300 px-1">
            Common
          </span>
        )}
      </div>
      <div className="text-[10px] font-mono text-gray-500 mt-1">
        {location.secondary}
      </div>
      {room.type === "bedroom" && (
        <div className="text-[10px] font-mono text-gray-500 mt-0.5">
          {room.bathroom === "ensuite" ? "Ensuite" : "Shared bath"} ·{" "}
          {room.key_status}
        </div>
      )}
      <div className="flex flex-wrap gap-1 mt-2">
        {flags.rebooking && <FlagPip kind="rebooking" />}
        {flags.storage && <FlagPip kind="storage" />}
        {flags.summer_stay && <FlagPip kind="summer_stay" />}
        {flags.refurbishment && <FlagPip kind="refurbishment" />}
        {(flags.occupied_override ?? room.key_status === "Room Occupied") && <FlagPip kind="occupied" />}
        {room.priority && <span className="flag-pip" style={{color:"#B91C1C"}}>Priority</span>}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2"><AssignmentBadge assignment={room.stages?.[room.current_stage]?.assignment||room.assignment}/><span className="text-[10px] font-semibold uppercase text-slate-500">{room.stages?.[room.current_stage]?.status?.replaceAll("_"," ")||"Active"}</span></div>
    </button>
  );
}
